import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { supabase } from '../../lib/supabase';
import { siteCopy } from '../../content/siteCopy';

const StoreNotifyContext = createContext(null);

export const StoreNotifyProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const { storeNotify, footer } = siteCopy;

  const openStoreNotify = useCallback(() => {
    setStatus(null);
    setOpen(true);
  }, []);

  const closeStoreNotify = useCallback(() => {
    setOpen(false);
    setStatus(null);
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeStoreNotify();
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, closeStoreNotify]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (honeypot) return;

    const value = (email || '').trim().toLowerCase();
    if (!value) return;

    if (!supabase) {
      setStatus('error');
      return;
    }

    setLoading(true);
    setStatus(null);

    const { error } = await supabase.from('newsletter_signups').insert({
      email: value,
      website: 'store_notify',
    });

    setLoading(false);

    if (error) {
      setStatus('error');
      return;
    }

    setStatus('success');
    setEmail('');
  };

  return (
    <StoreNotifyContext.Provider value={{ openStoreNotify }}>
      {children}
      {open ? (
        <>
          <div
            className="modal-backdrop show store_notify_backdrop"
            onClick={closeStoreNotify}
            aria-hidden="true"
          />
          <div
            className="modal d-block store_notify_modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="store-notify-title"
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title" id="store-notify-title">
                    {storeNotify.title}
                  </h5>
                  <button
                    type="button"
                    className="close"
                    onClick={closeStoreNotify}
                    aria-label="Close"
                  >
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  <p>{storeNotify.body}</p>
                  <form onSubmit={handleSubmit}>
                    <input
                      type="text"
                      name="website"
                      tabIndex={-1}
                      autoComplete="off"
                      value={honeypot}
                      onChange={(e) => setHoneypot(e.target.value)}
                      style={{
                        position: 'absolute',
                        left: '-9999px',
                        width: 1,
                        height: 1,
                        opacity: 0,
                      }}
                      aria-hidden="true"
                    />
                    <div className="form-group store_notify_field">
                      <input
                        type="email"
                        className="form-control"
                        placeholder={storeNotify.placeholder}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={loading || status === 'success'}
                        required
                      />
                      <button
                        type="submit"
                        className="btn puprple_btn"
                        disabled={loading || status === 'success'}
                      >
                        {storeNotify.submitLabel}
                      </button>
                    </div>
                    {status === 'success' ? (
                      <p className="store_notify_status store_notify_status--success">
                        {storeNotify.success}
                      </p>
                    ) : null}
                    {status === 'error' ? (
                      <p className="store_notify_status store_notify_status--error">
                        {storeNotify.error}
                      </p>
                    ) : null}
                    <p className="store_notify_legal">{footer.subscribeLegal}</p>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </StoreNotifyContext.Provider>
  );
};

export const useStoreNotify = () => {
  const ctx = useContext(StoreNotifyContext);
  if (!ctx) {
    throw new Error('useStoreNotify must be used within StoreNotifyProvider');
  }
  return ctx;
};
